import { FirstSessionPromise } from "@/components/site/FirstSessionPromise";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Sparkles,
  Search,
  MessageSquare,
  Brain,
  ClipboardCheck,
  ShieldCheck,
  CheckCircle2,
  FileCheck2,
  ArrowRight,
  GraduationCap,
} from "lucide-react";
import { site } from "@/lib/site";
import { openPlanChat } from "@/lib/plans";

export const Route = createFileRoute("/full-support")({
  head: () => ({
    meta: [
      { title: "F1 Visa Full Support - First-Time Coaching | WeLivdIt" },
      {
        name: "description",
        content:
          "First-time US visa applicant? 4 mocks, DS-160 review, profile analysis, and officer-logic strategy - end-to-end prep to interview day.",
      },
      {
        name: "keywords",
        content:
          "F1 visa coaching, first time visa applicant, fresher visa interview, US student visa prep, DS-160 review",
      },
      { property: "og:title", content: "F1 Visa Full Support - First-Time Applicant Coaching" },
      {
        property: "og:description",
        content:
          "End-to-end visa prep for first-time applicants: 4 mocks, DS-160 review, profile analysis, officer-logic strategy.",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://welivdit.com/full-support" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/full-support" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "F1 Visa Full Support",
          provider: { "@type": "Organization", name: "WeLivdIt" },
          serviceType: "First-time US student visa interview coaching",
          offers: { "@type": "Offer", price: "4500", priceCurrency: "INR" },
          areaServed: "Worldwide",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FullSupportPage,
});

const includes = [
  {
    icon: MessageSquare,
    title: "4 One-on-One Mock Interviews",
    desc: "Four live mocks with detailed feedback. We start at your level and ramp up until you're handling tough officer-style questioning calmly.",
  },
  {
    icon: FileCheck2,
    title: "Full DS-160 Form Guidance",
    desc: "Line-by-line review of every field. We catch the small inconsistencies that quietly tank first-time interviews.",
  },
  {
    icon: Search,
    title: "Profile Analysis",
    desc: "Academics, sponsor, finances, ties to home country, gap years - we map your full profile and surface anything that needs a stronger answer.",
  },
  {
    icon: Brain,
    title: "Visa Officer Logic & Strategy",
    desc: "1-on-1 deep dive on how officers actually evaluate F1 applicants - and how to frame your story so it lands the first time.",
  },
  {
    icon: Sparkles,
    title: "Spontaneous Question Training",
    desc: "Curveball drills. We throw the questions you didn't see coming so the real interview feels easy.",
  },
  {
    icon: ClipboardCheck,
    title: "Documentation Assistance",
    desc: "What to carry, what order, what officers actually look at - and the 24h-before-interview checklist.",
  },
];

const faqs = [
  {
    q: "How long before my interview should I start Full Support?",
    a: "Ideally 3–6 weeks out. That gives us time for DS-160 review, profile analysis, and 4 mocks with real practice between them. We can compress the timeline if your interview is closer - message us first so we can plan it properly.",
  },
  {
    q: "I've never given a visa interview before - is this enough?",
    a: "Yes - Full Support is built specifically for first-timers. By the end you'll have a tight DS-160, a clear story, 4 mocks of practice, and an answer prepared for every red flag in your profile.",
  },
  {
    q: "Do you guarantee approval?",
    a: "No - and no honest Specialist can. The decision is the officer's. What we guarantee is that you walk in with a clean DS-160, sharp answers, and the calmness that comes from real practice. That's what moves the odds.",
  },
  {
    q: "How is this different from Basic Support?",
    a: "Basic is 2 mocks only - for confident applicants who just want practice. Full adds DS-160 review, profile analysis, officer-logic strategy, and 2 extra mocks. If it's your first interview, pick Full.",
  },
  {
    q: "Can I upgrade to Refusal Support if I get refused?",
    a: "Yes. You can easily upgrade at any time by enrolling directly in the Refusal Support plan right here on our website.",
  },
];

function FullSupportPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-hero">
        <div className="mx-auto max-w-5xl px-5 pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-900 text-xs font-medium">
            <GraduationCap className="h-3.5 w-3.5" /> Most popular · For first-time applicants
          </div>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-balance max-w-3xl">
            First US visa interview? Let's do it right the first time.
          </h1>
          <p className="mt-5 text-lg text-foreground/70 max-w-2xl leading-relaxed">
            Full Support is end-to-end prep for first-timers - 4 one-on-one mocks, full DS-160 review, profile analysis,
            and an officer-logic strategy session. Walk in calm, answer sharp, leave with the visa.
          </p>
          <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" /> Backed by The WeLivdIt Method
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => openPlanChat("full")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Enroll in Full Support <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            ₹4,500 one-time · 4 mocks · DS-160 review · Profile analysis included
          </p>
          <FirstSessionPromise variant="inline" className="mt-3" />

        </div>
      </section>

      {/* Why first-timers struggle */}
      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Why first interviews go wrong</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            Most refusals aren't about your profile.
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            They're about how the story is told. A strong applicant with a vague answer loses to an average applicant
            with a sharp one. Full Support fixes the delivery, the DS-160, and the strategy - together.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            "DS-160 inconsistencies the officer spots in 30 seconds",
            "Vague answer for 'why this university' and 'why this course'",
            "Weak or unclear sponsor / financial story",
            "No prepared answer for ties to home country",
            "Memorised scripts that fall apart on follow-up questions",
            "Nervous delivery, long pauses, filler words",
          ].map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
              <Sparkles className="h-4 w-4 text-orange-600 shrink-0 mt-0.5" />
              <span className="text-sm text-foreground/80">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-5xl px-5 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">What's included</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
              Everything a first-timer needs.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {includes.map((it) => (
              <div key={it.title} className="rounded-3xl border border-border bg-card p-6">
                <div className="h-11 w-11 rounded-2xl bg-accent grid place-items-center text-primary">
                  <it.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{it.title}</h3>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="mx-auto max-w-5xl px-5 py-16">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">How it works</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight">
            From enrollment to interview day, in 4 steps.
          </h2>
        </div>
        <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[
            {
              n: 1,
              title: "Profile & DS-160 audit",
              desc: "We review your profile, financials, and DS-160 line by line. Every weak link is surfaced and fixed.",
            },
            {
              n: 2,
              title: "Strategy session",
              desc: "1-on-1 deep dive on visa officer logic and how to frame your answers using a response framework that holds up.",
            },
            {
              n: 3,
              title: "4 escalating mocks",
              desc: "Easy → realistic → tough → curveball. We push until the real interview feels routine.",
            },
            {
              n: 4,
              title: "Interview-day plan",
              desc: "Documents, opening line, body language, what to volunteer, what to keep brief. Nothing left to chance.",
            },
          ].map((step) => (
            <li key={step.n} className="rounded-3xl border border-border bg-card p-6">
              <div className="h-10 w-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-semibold">
                {step.n}
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold">{step.title}</h3>
              <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{step.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Who it's for */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 text-emerald-700 text-sm font-semibold">
              <CheckCircle2 className="h-4 w-4" /> This is for you if
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/80">
              {[
                "You're a first-time US visa applicant",
                "You want DS-160 review + profile analysis + mocks together",
                "Your interview is 2+ weeks away",
                "You want a real Specialist, not a recorded course",
              ].map((i) => (
                <li key={i} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                  {i}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border bg-card p-7">
            <div className="inline-flex items-center gap-2 text-amber-700 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> Pick a different plan if
            </div>
            <ul className="mt-4 space-y-3 text-sm text-foreground/80">
              {[
                "You've already been refused - go with Refusal Support instead",
                "You're confident and just need 2 mocks for practice - Basic Support is enough",
                "Your interview is in less than 5 days (message us first)",
                "You need help with SEVIS, I-20, or admissions (we don't do those)",
              ].map((i) => (
                <li key={i} className="flex gap-2">
                  <Sparkles className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  {i}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <div className="text-center">
            <p className="text-sm font-medium text-primary uppercase tracking-wider">FAQ</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Common questions about Full Support
            </h2>
          </div>
          <div className="mt-10 space-y-4">
            {faqs.map((f) => (
              <details key={f.q} className="group rounded-2xl border border-border bg-card p-5">
                <summary className="cursor-pointer list-none font-display text-base font-semibold flex items-center justify-between">
                  {f.q}
                  <span className="ml-4 text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-ink text-ink-foreground">
        <div className="mx-auto max-w-3xl px-5 py-20 text-center">
          <ShieldCheck className="h-10 w-10 text-primary mx-auto" />
          <h2 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight">
            Walk in ready. Walk out with the visa.
          </h2>
          <p className="mt-4 text-ink-foreground/70 max-w-xl mx-auto">
            ₹4,500 · 4 one-on-one mocks · DS-160 review · Profile analysis · Officer-logic strategy. A Specialist reaches out
            on WhatsApp within 24 hours of enrollment.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => openPlanChat("full")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Enroll in Full Support <ArrowRight className="h-4 w-4" />
            </button>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full border border-white/20 bg-white/[0.04] text-ink-foreground font-medium hover:bg-white/[0.08] transition"
            >
              Compare all plans
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
